/*!
 * BOPF Zara Wishlist Heart   [v10 - mobile nav heart support]
 *
 * Replaces SWYM wishlist hearts on product and collection pages with
 * Zara-owned hearts. Click opens an editorial email-capture popup
 * (Hadiza voice), posts to /zara/wishlist/add, and routes to Sheets,
 * Omnisend, and a Resend confirmation email.
 *
 * SWYM continues to run in parallel during the transition period; we
 * just hide its UI. When SWYM is removed, only our hearts remain.
 *
 * Install:
 *   1. Save this file in the theme as: assets/bopf-wishlist-heart.js
 *   2. Add to layout/theme.liquid, just before </body>:
 *        <script src="{{ 'bopf-wishlist-heart.js' | asset_url }}?v=1" defer></script>
 *   3. Remove any older bopf-wishlist.js include from theme.liquid.
 *      (The older file intercepts SWYM's _swat.addToWishList, which we
 *       no longer want now that we're replacing SWYM's UI entirely.)
 */

(function() {
  'use strict';

  var ZARA_BACKEND  = 'https://bopf-concierge.onrender.com';
  var STORAGE_EMAIL = 'bopf_zara_email';
  var STORAGE_SAVED = 'bopf_zara_saved';   // JSON: { handle: timestamp_ms }
  var STORAGE_DECLINED = 'bopf_zara_email_declined_at';
  var DECLINED_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

  function log() {
    try { console.log.apply(console, ['[bopf-heart]'].concat([].slice.call(arguments))); } catch(e) {}
  }

  // ── Storage helpers ────────────────────────────────────────────────
  function getEmail() {
    try { return localStorage.getItem(STORAGE_EMAIL) || ''; } catch(e) { return ''; }
  }
  function setEmail(email) {
    try { localStorage.setItem(STORAGE_EMAIL, email); } catch(e) {}
  }
  function getSavedMap() {
    try {
      var raw = localStorage.getItem(STORAGE_SAVED);
      return raw ? JSON.parse(raw) : {};
    } catch(e) { return {}; }
  }
  function setSavedMap(m) {
    try { localStorage.setItem(STORAGE_SAVED, JSON.stringify(m)); } catch(e) {}
  }
  function isSaved(handle) {
    var m = getSavedMap();
    return !!(handle && m[handle]);
  }
  function markSaved(handle) {
    var m = getSavedMap();
    m[handle] = Date.now();
    setSavedMap(m);
    fireWishlistChanged();
  }
  function markUnsaved(handle) {
    var m = getSavedMap();
    delete m[handle];
    setSavedMap(m);
    fireWishlistChanged();
  }
  function fireWishlistChanged() {
    try {
      window.dispatchEvent(new CustomEvent('bopf-wishlist-changed', {
        detail: { count: Object.keys(getSavedMap()).length }
      }));
    } catch(e) { /* old browsers without CustomEvent constructor */ }
  }
  function recentlyDeclined() {
    try {
      var t = parseInt(localStorage.getItem(STORAGE_DECLINED) || '0', 10);
      return t && (Date.now() - t) < DECLINED_COOLDOWN_MS;
    } catch(e) { return false; }
  }
  function markDeclined() {
    try { localStorage.setItem(STORAGE_DECLINED, String(Date.now())); } catch(e) {}
  }

  // ── Page context detection ─────────────────────────────────────────
  function getPageType() {
    var p = window.location.pathname;
    if (/\/products\/[^\/]+/.test(p)) return 'product';
    if (/\/collections\//.test(p)) return 'collection';
    if (/\/search/.test(p)) return 'search';
    return 'other';
  }

  function getProductHandleFromUrl(url) {
    var m = String(url || '').match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : '';
  }

  // Read product context for the CURRENT product page from the page DOM.
  // SWYM populates window.SwymPageData but we don't depend on it - we
  // read from standard Shopify meta tags so this works even after SWYM
  // is removed entirely.
  function readCurrentProductContext() {
    var ctx = { handle: '', title: '', image: '', price: null, url: '' };

    // Prefer the canonical URL over window.location.pathname. Shopify
    // sets <link rel="canonical"> to the authoritative product URL, which
    // stays correct even if customers arrive via stale links with
    // outdated handles. Falling back to pathname-derived handles bakes
    // in stale identifiers that 404 from the products.js endpoint later.
    var canonicalLink = document.querySelector('link[rel="canonical"]');
    var canonicalHref = canonicalLink ? canonicalLink.getAttribute('href') : '';
    if (canonicalHref) {
      ctx.handle = getProductHandleFromUrl(canonicalHref);
      ctx.url = canonicalHref;
    }
    // Fallback: derive from current location.
    if (!ctx.handle) {
      ctx.handle = getProductHandleFromUrl(window.location.pathname);
      ctx.url = window.location.origin + window.location.pathname;
    }

    // Defensive title extraction: some themes inject markup into og:title.
    // We strip any HTML tags (and collapse whitespace) before using it,
    // so the email subject and body never contain raw <div> fragments.
    var rawTitle = '';
    var ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) rawTitle = ogTitle.getAttribute('content') || '';
    if (!rawTitle) rawTitle = (document.title || '').split('|')[0];
    ctx.title = sanitizeText(rawTitle);

    var ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) ctx.image = (ogImage.getAttribute('content') || '').trim();

    var priceMeta = document.querySelector('meta[property="product:price:amount"]');
    if (priceMeta) ctx.price = parseFloat(priceMeta.getAttribute('content')) || null;

    return ctx;
  }

  // Strip HTML tags, collapse whitespace, trim. Used wherever we pull
  // a "title" from the page that might be polluted by theme template bugs.
  function sanitizeText(s) {
    if (!s) return '';
    return String(s)
      .replace(/<[^>]*>/g, ' ')        // strip HTML tags
      .replace(/&nbsp;/gi, ' ')        // common entity
      .replace(/&amp;/g, '&')          // ampersand entity
      .replace(/&lt;/g, '<')           // these last two only for display
      .replace(/&gt;/g, '>')
      .replace(/\s+/g, ' ')            // collapse whitespace
      .trim();
  }

  // Read product context from a collection card element. Different
  // themes use different markup; we try a handful of common patterns.
  function readCardContext(cardEl) {
    var ctx = { handle: '', title: '', image: '', price: null, url: '' };
    var link = cardEl.querySelector('a[href*="/products/"]');
    if (link) {
      ctx.url = link.href;
      ctx.handle = getProductHandleFromUrl(link.getAttribute('href'));
    }
    var img = cardEl.querySelector('img');
    if (img) {
      ctx.image = img.currentSrc || img.src || img.getAttribute('data-src') || '';
      // Strip Shopify-specific size suffixes like _200x200 to get a usable image
      ctx.image = ctx.image.replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original|\d+x\d*|\d+x\d+)\./, '.');
    }
    // Title is usually the link text or a sibling heading.
    var titleEl = cardEl.querySelector('h2, h3, .product-card__title, [class*="card__title"], [class*="product-title"]');
    if (titleEl) ctx.title = sanitizeText(titleEl.textContent);
    if (!ctx.title && link) ctx.title = sanitizeText(link.textContent);
    // Price: look for an element containing "Dhs." or "AED"
    var priceEl = cardEl.querySelector('[class*="price"]');
    if (priceEl) {
      var priceText = (priceEl.textContent || '').replace(/[^0-9.,]/g, '').replace(/,/g, '');
      var p = parseFloat(priceText);
      if (!isNaN(p) && p > 0) ctx.price = p;
    }
    return ctx;
  }

  // ── Backend call ───────────────────────────────────────────────────
  function postWishlistAdd(email, ctx, consentMarketing) {
    var payload = JSON.stringify({
      email: email,
      product_handle: ctx.handle,
      product_title: ctx.title,
      product_price_aed: ctx.price || null,
      product_image: ctx.image || '',
      product_url: ctx.url || '',
      source: 'wishlist_heart',
      consent_marketing: !!consentMarketing
    });
    return fetch(ZARA_BACKEND + '/zara/wishlist/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload
    }).then(function(r){ return r.json().catch(function(){ return {ok:false}; }); });
  }

  // ── Style injection ────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('bopf-heart-styles')) return;
    var s = document.createElement('style');
    s.id = 'bopf-heart-styles';
    s.textContent = '' +

      // ── Hide ALL SWYM wishlist UI on the storefront ──
      // SWYM hearts use a variety of class patterns. Some installs use
      // swym-* prefixes, others use atw-* (Add To Wishlist). We hide
      // every known variant; if a future SWYM update introduces new
      // ones we add them here.
      '.swym-wishlist-button-bar,' +
      '.swym-wishlist-button,' +
      '.swym-button.swym-add-to-wishlist-view-product,' +
      '.swym-add-to-wishlist,' +
      '.swym-add-to-wishlist-view-product,' +
      '.swym-collection-add-to-wishlist-view-product,' +
      '.swym-collection-add-to-wishlist-view-collection,' +
      '.swym-button.swym-bulk-add-to-cart-button-cart,' +
      '[data-swaction="addToWishlist"],' +
      '.swym-add-to-wishlist-button,' +
      '.swym-emt-add-to-wishlist,' +
      // atw-* variant (this BOPF install uses it)
      '#swym-atw-pdp-button,' +
      '.atw-button,' +
      '.atw-button-icon,' +
      '[id^="swym-atw-"],' +
      '[class*="atw-button"],' +
      // Nav heart icon (header wishlist launcher).
      // We hide both the empty SWYM placeholder slot AND any rendered
      // SWYM heart elements. Our own heart is injected next to the cart
      // icon on the right, regardless of where SWYM\'s placeholder sits.
      'a[href="#swym-wishlist"],' +
      '.swym-wishlist,' +
      '.swym-launcher-header-icon,' +
      '[id^="swym-inject-header"] {' +
        'display: none !important; visibility: hidden !important;' +
      '}' +

      // ── Collection card heart ──
      '.bopf-card-heart {' +
        'position: absolute; top: 12px; right: 12px;' +
        'width: 32px; height: 32px;' +
        'background: rgba(255,255,255,0.85);' +
        'border: none;' +
        'border-radius: 50%;' +
        'cursor: pointer;' +
        'display: flex; align-items: center; justify-content: center;' +
        'padding: 0;' +
        'transition: background 0.15s, transform 0.1s;' +
        'z-index: 5;' +
      '}' +
      '.bopf-card-heart:hover {' +
        'background: rgba(255,255,255,1);' +
      '}' +
      '.bopf-card-heart:active {' +
        'transform: scale(0.92);' +
      '}' +
      '.bopf-card-heart svg {' +
        'width: 20px; height: 20px;' +
        'stroke: #1F1A17;' +
        'stroke-width: 1.6;' +
        'fill: none;' +
        'transition: fill 0.18s, stroke 0.18s;' +
      '}' +
      '.bopf-card-heart.is-saved svg {' +
        'fill: #A0563E;' +
        'stroke: #A0563E;' +
      '}' +

      // Card position context: we make sure the parent has a relative
      // anchor so our absolutely-positioned heart sits in the right corner.
      '.bopf-card-anchor {' +
        'position: relative !important;' +
      '}' +

      // ── Product page heart (Option A: next to price, not a full button) ──
      // Mirrors the collection card heart, slightly larger. Sits inline
      // with the price area so it doesn't steal real estate from
      // Add to cart / View this product. The customer recognises the
      // heart pattern from collection pages, no extra learning required.
      '.bopf-product-heart {' +
        'display: inline-flex; align-items: center; justify-content: center;' +
        'width: 38px; height: 38px;' +
        'background: transparent;' +
        'border: 1px solid #d6cfc4;' +
        'border-radius: 50%;' +
        'cursor: pointer;' +
        'padding: 0;' +
        'margin-left: 12px;' +
        'vertical-align: middle;' +
        'transition: border-color 0.18s, background 0.18s, transform 0.1s;' +
        'flex-shrink: 0;' +
      '}' +
      '.bopf-product-heart:hover {' +
        'border-color: #A0563E;' +
        'background: rgba(160, 86, 62, 0.05);' +
      '}' +
      '.bopf-product-heart:active {' +
        'transform: scale(0.92);' +
      '}' +
      '.bopf-product-heart svg {' +
        'width: 20px; height: 20px;' +
        'stroke: #1F1A17;' +
        'stroke-width: 1.6;' +
        'fill: none;' +
        'transition: fill 0.25s ease, stroke 0.18s;' +
      '}' +
      '.bopf-product-heart:hover svg {' +
        'stroke: #A0563E;' +
      '}' +
      '.bopf-product-heart.is-saved {' +
        'border-color: #A0563E;' +
        'background: rgba(160, 86, 62, 0.08);' +
      '}' +
      '.bopf-product-heart.is-saved svg {' +
        'fill: #A0563E;' +
        'stroke: #A0563E;' +
      '}' +
      // Click pulse animation: heart briefly scales up then settles.
      // Triggered by JS adding the .pulse class for ~280ms.
      '.bopf-product-heart.pulse svg, .bopf-card-heart.pulse svg {' +
        'animation: bopfHeartPulse 0.28s ease;' +
      '}' +
      '@keyframes bopfHeartPulse {' +
        '0%   { transform: scale(1); }' +
        '40%  { transform: scale(1.25); }' +
        '100% { transform: scale(1); }' +
      '}' +
      // Small accompanying label that appears alongside the heart on
      // larger screens, kept hidden on mobile to save space.
      '.bopf-product-heart-label {' +
        'display: inline-block;' +
        'margin-left: 8px;' +
        'font-size: 11px;' +
        'letter-spacing: 0.08em;' +
        'text-transform: uppercase;' +
        'color: #888;' +
        'font-style: italic;' +
        'font-family: Georgia, serif;' +
        'vertical-align: middle;' +
        'transition: color 0.18s;' +
      '}' +
      '.bopf-product-heart-wrap:hover .bopf-product-heart-label {' +
        'color: #A0563E;' +
      '}' +
      '.bopf-product-heart-wrap.is-saved .bopf-product-heart-label {' +
        'color: #A0563E;' +
      '}' +
      '@media (max-width: 640px) {' +
        '.bopf-product-heart-label { display: none; }' +
      '}' +
      '.bopf-product-heart-wrap {' +
        'display: inline-flex; align-items: center;' +
        'margin-top: 8px;' +
      '}' +

      // ── Old full-width button kept as fallback (used if we cannot find
      //   a price-area injection point on this theme) ──
      '.bopf-product-button {' +
        'display: flex; align-items: center; justify-content: center;' +
        'gap: 10px;' +
        'width: 100%;' +
        'padding: 14px 24px;' +
        'background: #1F1A17;' +
        'color: #FAF6F0;' +
        'border: 1px solid #1F1A17;' +
        'font-family: inherit;' +
        'font-size: 12px;' +
        'letter-spacing: 0.18em;' +
        'text-transform: uppercase;' +
        'cursor: pointer;' +
        'margin: 12px 0;' +
        'transition: background 0.15s, color 0.15s;' +
      '}' +
      '.bopf-product-button:hover {' +
        'background: #2c2521;' +
      '}' +
      '.bopf-product-button:active {' +
        'transform: scale(0.99);' +
      '}' +
      '.bopf-product-button svg {' +
        'width: 16px; height: 16px;' +
        'stroke: #FAF6F0;' +
        'stroke-width: 1.6;' +
        'fill: none;' +
        'transition: fill 0.18s;' +
      '}' +
      '.bopf-product-button.is-saved {' +
        'background: #FAF6F0;' +
        'color: #1F1A17;' +
        'border-color: #1F1A17;' +
      '}' +
      '.bopf-product-button.is-saved svg {' +
        'fill: #A0563E;' +
        'stroke: #A0563E;' +
      '}' +

      // ── Nav heart (header wishlist launcher) ─────────────────────
      // Replaces SWYM\'s nav heart with our own. Sits as an inline
      // element so it can fit alongside the cart/account icons. The
      // count badge appears only when the customer has saved items,
      // and pulses briefly whenever the count changes.
      '.bopf-nav-heart {' +
        'display: inline-flex; align-items: center; justify-content: center;' +
        'position: relative;' +
        'width: 36px; height: 36px;' +
        'background: transparent; border: none; padding: 0;' +
        'color: inherit; text-decoration: none; cursor: pointer;' +
        'vertical-align: middle;' +
        'transition: transform 0.12s ease;' +
      '}' +
      '.bopf-nav-heart svg {' +
        'width: 22px; height: 22px;' +
        'stroke: currentColor; stroke-width: 1.6; fill: none;' +
        'transition: fill 0.22s ease, stroke 0.22s ease, transform 0.22s ease;' +
      '}' +
      '.bopf-nav-heart:hover svg {' +
        'stroke: #A0563E;' +
      '}' +
      '.bopf-nav-heart.has-saved svg {' +
        'fill: #A0563E; stroke: #A0563E;' +
      '}' +
      '.bopf-nav-heart-badge {' +
        'position: absolute; top: 2px; right: 0;' +
        'min-width: 16px; height: 16px; padding: 0 4px;' +
        'background: #A0563E; color: #FAF6F0;' +
        'border-radius: 8px;' +
        'font-size: 10px; font-weight: 600; line-height: 16px;' +
        'text-align: center;' +
        'font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;' +
        'box-sizing: border-box;' +
        'pointer-events: none;' +
        'opacity: 0; transform: scale(0.6);' +
        'transition: opacity 0.18s ease, transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1);' +
      '}' +
      '.bopf-nav-heart.has-saved .bopf-nav-heart-badge {' +
        'opacity: 1; transform: scale(1);' +
      '}' +
      // Pulse triggered programmatically when count changes upward.
      '.bopf-nav-heart.pulse svg {' +
        'animation: bopfNavHeartPulse 0.42s cubic-bezier(0.34, 1.56, 0.64, 1);' +
      '}' +
      '.bopf-nav-heart.pulse .bopf-nav-heart-badge {' +
        'animation: bopfNavBadgePop 0.42s cubic-bezier(0.34, 1.56, 0.64, 1);' +
      '}' +
      '@keyframes bopfNavHeartPulse {' +
        '0%   { transform: scale(1); }' +
        '40%  { transform: scale(1.32); }' +
        '100% { transform: scale(1); }' +
      '}' +
      '@keyframes bopfNavBadgePop {' +
        '0%   { transform: scale(1); }' +
        '40%  { transform: scale(1.4); }' +
        '100% { transform: scale(1); }' +
      '}' +

      // Mobile-specific tweaks: heart sits as a sibling of .mobile-menu__button
      // items inside .mobile-menu, so we need to match their tap-target
      // dimensions (44x44 is the iOS HIG minimum, most themes use that).
      '.bopf-nav-heart--mobile {' +
        'width: 44px; height: 44px;' +
        'flex: 0 0 auto;' +
      '}' +
      '.bopf-nav-heart--mobile svg {' +
        'width: 22px; height: 22px;' +
      '}' +
      '.bopf-nav-heart--mobile .bopf-nav-heart-badge {' +
        'top: 6px; right: 4px;' +
      '}' +

      // ── Modal popup (matches the wishlist popup we already built) ──
      '.bopf-wl-overlay{position:fixed;top:0;left:0;right:0;bottom:0;z-index:99998;background:rgba(20,15,12,0.55);backdrop-filter:blur(2px);display:flex;align-items:center;justify-content:center;padding:20px;animation:bopfWlFade .18s ease}' +
      '@keyframes bopfWlFade{from{opacity:0}to{opacity:1}}' +
      '.bopf-wl-card{background:#FAF6F0;border-radius:8px;width:100%;max-width:420px;padding:30px 28px 26px;box-shadow:0 20px 60px rgba(20,15,12,.25);position:relative;font-family:-apple-system,BlinkMacSystemFont,"Helvetica Neue",Arial,sans-serif;color:#1F1A17}' +
      '.bopf-wl-x{position:absolute;top:10px;right:12px;background:none;border:none;font-size:24px;line-height:1;color:#999;cursor:pointer;padding:6px 10px;border-radius:50%}' +
      '.bopf-wl-x:hover{background:rgba(0,0,0,.05);color:#1F1A17}' +
      '.bopf-wl-eyebrow{font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:#A0563E;margin:0 0 8px 0}' +
      '.bopf-wl-headline{font-family:Georgia,"Times New Roman",serif;font-size:24px;line-height:1.25;color:#1F1A17;margin:0 0 12px 0;font-weight:400}' +
      '.bopf-wl-headline em{font-style:italic;color:#A0563E;position:relative;display:inline-block}' +
      '.bopf-wl-headline em:after{content:"";position:absolute;left:0;right:0;bottom:-4px;height:6px;background-image:url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 6"><path d="M2 3 Q 14 1, 24 3 T 44 3 T 64 3 T 88 3" stroke="%23A0563E" stroke-width="1.4" fill="none" stroke-linecap="round"/></svg>\');background-repeat:no-repeat;background-size:100% 100%}' +
      '.bopf-wl-body{font-size:14px;line-height:1.55;color:#3a3530;margin:0 0 18px 0}' +
      '.bopf-wl-input{width:100%;padding:12px 14px;font-size:15px;font-family:inherit;color:#1F1A17;border:1px solid #d6cfc4;background:#fff;border-radius:4px;box-sizing:border-box;margin-bottom:12px;transition:border-color .15s}' +
      '.bopf-wl-input:focus{outline:none;border-color:#A0563E}' +
      '.bopf-wl-checkbox-row{display:flex;gap:8px;align-items:flex-start;margin-bottom:18px;font-size:13px;color:#666;line-height:1.45}' +
      '.bopf-wl-checkbox-row input{margin-top:3px;flex-shrink:0;cursor:pointer}' +
      '.bopf-wl-checkbox-row label{cursor:pointer}' +
      '.bopf-wl-actions{display:flex;gap:10px;align-items:center}' +
      '.bopf-wl-btn{padding:12px 22px;font-size:13px;letter-spacing:0.05em;font-family:inherit;border-radius:4px;cursor:pointer;border:none;transition:all .12s;font-weight:500}' +
      '.bopf-wl-btn-primary{background:#A0563E;color:#fff;flex:1}' +
      '.bopf-wl-btn-primary:hover{background:#8a4732}' +
      '.bopf-wl-btn-primary:disabled{opacity:.5;cursor:not-allowed}' +
      '.bopf-wl-btn-text{background:none;color:#999;text-decoration:underline;padding:8px 4px}' +
      '.bopf-wl-btn-text:hover{color:#1F1A17}' +
      '.bopf-wl-error{font-size:13px;color:#a8443a;margin:8px 0 0 0;min-height:18px}' +
      '.bopf-wl-fineprint{font-size:11px;color:#999;margin-top:14px;line-height:1.4}' +
      '.bopf-wl-fineprint a{color:#666}' +
      '@media (max-width:480px){.bopf-wl-card{padding:24px 20px 22px}.bopf-wl-headline{font-size:21px}}';

    document.head.appendChild(s);
  }

  // ── Heart icon SVGs ────────────────────────────────────────────────
  var HEART_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true">' +
    '<path d="M12 21s-7-4.5-7-10.5C5 7 7.5 5 10 5c1.5 0 2.6.7 3 1.5C13.4 5.7 14.5 5 16 5c2.5 0 5 2 5 5.5C21 16.5 12 21 12 21z" stroke-linejoin="round" stroke-linecap="round"/>' +
  '</svg>';

  // ── Modal popup ────────────────────────────────────────────────────
  function escapeHtml(s) {
    return String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  function openModal(ctx, onSubmit, onCancel) {
    injectStyles();
    var overlay = document.createElement('div');
    overlay.className = 'bopf-wl-overlay';
    overlay.innerHTML =
      '<div class="bopf-wl-card" role="dialog" aria-labelledby="bopf-wl-h">' +
        '<button class="bopf-wl-x" aria-label="Close" data-bopf-wl-close>&times;</button>' +
        '<p class="bopf-wl-eyebrow">A note from Hadiza</p>' +
        '<h2 class="bopf-wl-headline" id="bopf-wl-h">Save this piece, and I\'ll <em>let you know</em> when it\'s right.</h2>' +
        '<p class="bopf-wl-body">Drop your email and I\'ll personally reach out if the price comes down, if it\'s about to sell, or if I find something similar that suits you.</p>' +
        '<form data-bopf-wl-form>' +
          '<input class="bopf-wl-input" type="email" name="email" placeholder="your@email.com" required autocomplete="email" />' +
          '<div class="bopf-wl-checkbox-row">' +
            '<input type="checkbox" id="bopf-wl-mkt" name="marketing" />' +
            '<label for="bopf-wl-mkt">Yes, also send me new arrivals and BOPF news (you can unsubscribe anytime).</label>' +
          '</div>' +
          '<div class="bopf-wl-actions">' +
            '<button type="submit" class="bopf-wl-btn bopf-wl-btn-primary" data-bopf-wl-submit>Save & alert me</button>' +
            '<button type="button" class="bopf-wl-btn bopf-wl-btn-text" data-bopf-wl-skip>Skip</button>' +
          '</div>' +
          '<p class="bopf-wl-error" data-bopf-wl-err></p>' +
        '</form>' +
        '<p class="bopf-wl-fineprint">We only use your email for the alerts you\'ve opted into. No spam. <a href="/policies/privacy-policy" target="_blank">Privacy</a>.</p>' +
      '</div>';
    document.body.appendChild(overlay);

    function close() {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }

    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) { close(); if (onCancel) onCancel(); }
    });
    overlay.querySelector('[data-bopf-wl-close]').addEventListener('click', function() {
      close(); if (onCancel) onCancel();
    });
    overlay.querySelector('[data-bopf-wl-skip]').addEventListener('click', function() {
      close(); if (onCancel) onCancel();
    });

    var form = overlay.querySelector('[data-bopf-wl-form]');
    var emailInput = form.querySelector('input[name="email"]');
    var mktCheckbox = form.querySelector('input[name="marketing"]');
    var submitBtn = overlay.querySelector('[data-bopf-wl-submit]');
    var errEl = overlay.querySelector('[data-bopf-wl-err]');

    setTimeout(function(){ try { emailInput.focus(); } catch(e) {} }, 50);

    form.addEventListener('submit', function(e) {
      e.preventDefault();
      var email = (emailInput.value || '').trim().toLowerCase();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errEl.textContent = 'Please enter a valid email.';
        return;
      }
      errEl.textContent = '';
      submitBtn.disabled = true;
      submitBtn.textContent = 'Saving...';
      var consentMarketing = !!mktCheckbox.checked;
      close();
      onSubmit(email, consentMarketing);
    });

    function escClose(e) {
      if (e.key === 'Escape') {
        document.removeEventListener('keydown', escClose);
        if (overlay.parentNode) { close(); if (onCancel) onCancel(); }
      }
    }
    document.addEventListener('keydown', escClose);
  }

  // ── Click handler shared by both heart and button ──────────────────
  function handleSaveClick(ctx, heartEl) {
    if (!ctx.handle) {
      log('no product handle, skipping');
      return;
    }

    // Toggle behaviour: if already saved, unsave (local only - we don't
    // currently support remove on the backend, but we update the visual
    // state so the customer can re-save and trigger a fresh email if
    // they want to).
    if (isSaved(ctx.handle)) {
      markUnsaved(ctx.handle);
      heartEl.classList.remove('is-saved');
      var btnLabel = heartEl.querySelector('[data-bopf-label]');
      if (btnLabel) btnLabel.textContent = 'Add to wishlist';
      log('unsaved (local only):', ctx.handle);
      return;
    }

    var email = getEmail();
    if (email) {
      // Returning customer - silent capture.
      log('returning customer, silent capture:', ctx.handle);
      postWishlistAdd(email, ctx, false).then(function(d){ log('backend:', d); });
      markSaved(ctx.handle);
      heartEl.classList.add('is-saved');
      var btnLabel2 = heartEl.querySelector('[data-bopf-label]');
      if (btnLabel2) btnLabel2.textContent = 'Saved';
      return;
    }

    if (recentlyDeclined()) {
      // They've recently chosen not to share email. Save locally without
      // backend capture; future automation won't reach them but the
      // wishlist still works on this device.
      log('recently declined, local-only save:', ctx.handle);
      markSaved(ctx.handle);
      heartEl.classList.add('is-saved');
      var btnLabel3 = heartEl.querySelector('[data-bopf-label]');
      if (btnLabel3) btnLabel3.textContent = 'Saved';
      return;
    }

    // First-time: open popup for email capture.
    log('opening popup for:', ctx.handle);
    openModal(ctx,
      function onSubmitEmail(email, consentMarketing) {
        setEmail(email);
        markSaved(ctx.handle);
        heartEl.classList.add('is-saved');
        var btnLabel4 = heartEl.querySelector('[data-bopf-label]');
        if (btnLabel4) btnLabel4.textContent = 'Saved';
        postWishlistAdd(email, ctx, consentMarketing).then(function(d){ log('backend:', d); });
      },
      function onCancel() {
        markDeclined();
        // Still save locally so the customer's visual state respects
        // their click. They just don't get alerts.
        markSaved(ctx.handle);
        heartEl.classList.add('is-saved');
        var btnLabel5 = heartEl.querySelector('[data-bopf-label]');
        if (btnLabel5) btnLabel5.textContent = 'Saved';
        log('declined popup, local-only save:', ctx.handle);
      }
    );
  }

  // ── Product page: inject button ────────────────────────────────────
  function injectProductButton() {
    // Prevent duplicates whether we've injected the heart or the fallback button.
    if (document.querySelector('.bopf-product-heart-wrap, .bopf-product-button')) return;

    var ctx = readCurrentProductContext();
    if (!ctx.handle) {
      log('product page but no handle detected');
      return;
    }

    // PRIMARY APPROACH: small heart icon placed AFTER the price element.
    // This is the Option A design: a 38px outlined heart sits inline with
    // the price block, no full-width button stack. Discoverable, on-brand,
    // doesn't compete with Add to cart.
    var priceEl = document.querySelector(
      '.product-info .price, .product__price, [class*="product-price"]:not([class*="compare"]), ' +
      '[class*="product__pricing"], [data-product-price]'
    );

    // We want the heart to sit alongside the price text on the same visual
    // line. Find the parent that wraps the price line.
    var priceParent = priceEl ? (priceEl.closest('.price-area, .product__price, [class*="price"]') || priceEl.parentElement) : null;

    if (priceEl && priceParent) {
      var wrap = document.createElement('div');
      wrap.className = 'bopf-product-heart-wrap';
      if (isSaved(ctx.handle)) wrap.classList.add('is-saved');

      var heart = document.createElement('button');
      heart.type = 'button';
      heart.className = 'bopf-product-heart';
      heart.setAttribute('aria-label', isSaved(ctx.handle) ? 'Remove from wishlist' : 'Save to wishlist');
      if (isSaved(ctx.handle)) heart.classList.add('is-saved');
      heart.innerHTML = HEART_SVG;

      var label = document.createElement('span');
      label.className = 'bopf-product-heart-label';
      label.setAttribute('data-bopf-label', '');
      label.textContent = isSaved(ctx.handle) ? 'Saved' : 'Save this piece';

      wrap.appendChild(heart);
      wrap.appendChild(label);

      heart.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        // Pulse animation: brief class toggle so the heart visibly responds
        // before the popup or save state lands.
        heart.classList.add('pulse');
        setTimeout(function(){ heart.classList.remove('pulse'); }, 320);
        handleSaveClick(ctx, heart);
        // Sync the wrap and label to the heart's saved state after the click.
        // handleSaveClick will toggle is-saved on the heart; we mirror.
        setTimeout(function(){
          if (heart.classList.contains('is-saved')) {
            wrap.classList.add('is-saved');
            label.textContent = 'Saved';
          } else {
            wrap.classList.remove('is-saved');
            label.textContent = 'Save this piece';
          }
        }, 0);
      });

      // Place the wrap right after the price element (so it sits on the
      // line below if the price line is full, or wraps inline cleanly).
      // Use insertAdjacentElement('afterend') so it stays as a sibling.
      var anchor = priceEl.parentElement === priceParent ? priceEl : priceParent;
      anchor.insertAdjacentElement('afterend', wrap);
      log('injected heart icon next to price for', ctx.handle);
      return;
    }

    // FALLBACK: if we can't locate the price element on this theme, fall
    // back to the original full-width black button below the Add to cart
    // form. This keeps wishlist working even on themes that hide their
    // price markup behind unusual class names.
    log('price element not found, falling back to full-width button');
    var addToCartBtn = document.querySelector('button[name="add"], button[type="submit"][class*="add"], form[action*="/cart/add"] button[type="submit"]');
    var injectionParent = null;
    var injectionRef = null;

    if (addToCartBtn) {
      var formOrSection = addToCartBtn.closest('form') || addToCartBtn.parentElement;
      if (formOrSection) {
        injectionParent = formOrSection.parentElement;
        injectionRef = formOrSection.nextSibling;
      }
    }

    if (!injectionParent) {
      var swymZone = document.querySelector(
        '#swym-atw-pdp-button, .atw-button, ' +
        '.swym-wishlist-button-bar, .swym-add-to-wishlist-view-product'
      );
      if (swymZone && swymZone.parentNode) {
        injectionParent = swymZone.parentNode;
        injectionRef = swymZone.nextSibling;
      }
    }

    if (!injectionParent) {
      log('no injection target found on product page');
      return;
    }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bopf-product-button';
    if (isSaved(ctx.handle)) btn.classList.add('is-saved');
    btn.innerHTML = HEART_SVG + '<span data-bopf-label>' + (isSaved(ctx.handle) ? 'Saved' : 'Add to wishlist') + '</span>';
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      btn.classList.add('pulse');
      setTimeout(function(){ btn.classList.remove('pulse'); }, 320);
      handleSaveClick(ctx, btn);
    });

    injectionParent.insertBefore(btn, injectionRef);
    log('injected fallback product button for', ctx.handle);
  }

  // ── Collection cards: inject hearts ────────────────────────────────
  function injectCollectionHearts(root) {
    root = root || document;
    // Find every product card on the page. Theme-agnostic detection:
    // a card is anything containing a product link. We look for distinct
    // products by their /products/ link.
    var productLinks = root.querySelectorAll('a[href*="/products/"]');
    var processedHandles = {};

    for (var i = 0; i < productLinks.length; i++) {
      var link = productLinks[i];
      var handle = getProductHandleFromUrl(link.getAttribute('href'));
      if (!handle) continue;

      // Find the card boundary - usually the closest LI, ARTICLE, or
      // a div with "card" or "product" in its class name.
      var card = link.closest('li, article, [class*="product-card"], [class*="card"], [class*="grid__item"], [class*="grid-item"]');
      if (!card) continue;

      // Skip if this card already has our heart.
      if (card.querySelector('.bopf-card-heart')) continue;

      // Skip if it's the same product we just processed in this card -
      // some themes have multiple links per card (image + title).
      var cardKey = handle + '|' + (card._bopfId || (card._bopfId = Math.random()));
      if (processedHandles[cardKey]) continue;
      processedHandles[cardKey] = true;

      // Filter out cards that are obviously NOT product cards: search
      // suggestions, mini-cart, footer links, etc. Heuristic: must
      // contain an image AND a price-like text.
      var hasImg = !!card.querySelector('img');
      if (!hasImg) continue;

      var ctx = readCardContext(card);
      ctx.handle = handle;  // ensure handle is set
      if (!ctx.handle || !ctx.title) continue;

      card.classList.add('bopf-card-anchor');

      var heart = document.createElement('button');
      heart.type = 'button';
      heart.className = 'bopf-card-heart';
      heart.setAttribute('aria-label', isSaved(handle) ? 'Remove from wishlist' : 'Add to wishlist');
      if (isSaved(handle)) heart.classList.add('is-saved');
      heart.innerHTML = HEART_SVG;
      heart.addEventListener('click', function(c, h) {
        return function(e) {
          e.preventDefault();
          e.stopPropagation();
          h.classList.add('pulse');
          setTimeout(function(){ h.classList.remove('pulse'); }, 320);
          handleSaveClick(c, h);
        };
      }(ctx, heart));

      card.appendChild(heart);
    }
  }

  // ── MutationObserver for dynamic loads (infinite scroll, filters) ──
  var observerInstalled = false;
  function installObserver() {
    if (observerInstalled || !window.MutationObserver) return;
    observerInstalled = true;
    var pending = false;
    var observer = new MutationObserver(function() {
      if (pending) return;
      pending = true;
      // Debounce: re-scan once per animation frame, not per mutation.
      requestAnimationFrame(function() {
        pending = false;
        injectCollectionHearts();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ── Init ───────────────────────────────────────────────────────────
  function init() {
    log('init, page type:', getPageType());
    injectStyles();
    var pageType = getPageType();

    // Nav heart runs site-wide regardless of page type. It's the entry
    // point to /pages/saved from anywhere on the storefront.
    injectNavHeart();

    if (pageType === 'product') {
      injectProductButton();
      // Some themes lazy-render the product info, so retry briefly.
      var retries = 0;
      var retryTimer = setInterval(function() {
        retries++;
        if (document.querySelector('.bopf-product-button') || retries > 20) {
          clearInterval(retryTimer);
          return;
        }
        injectProductButton();
      }, 200);
    }

    // Collection-style heart injection: useful on collection pages,
    // search pages, and home pages with featured product grids. Run
    // it on every non-product page to catch all of these.
    if (pageType !== 'product') {
      injectCollectionHearts();
      installObserver();
    }
    // Also run on product pages to catch any "you might also like" grids.
    if (pageType === 'product') {
      setTimeout(function() {
        injectCollectionHearts();
        installObserver();
      }, 500);
    }
  }

  // ── Nav heart ──────────────────────────────────────────────────────
  // Drops a heart icon into the header nav alongside cart/account icons.
  // Reads the saved-count from localStorage so it stays in sync without
  // a server round-trip. Listens for storage events (other tabs) and a
  // custom 'bopf-wishlist-changed' event (same tab) to update reactively.
  function getSavedCount() {
    return Object.keys(getSavedMap()).length;
  }

  function findNavInjectionPoint() {
    // Both desktop nav (#NavStandard) and mobile nav (.mobile-menu) exist
    // in the DOM simultaneously - the theme uses CSS to show/hide based
    // on viewport. We try BOTH and inject into whichever is currently
    // visible, so the heart appears in the right nav for the device.
    // Order: mobile first (smaller viewports often have stricter constraints),
    // desktop second.

    // ── Mobile: .mobile-menu container with .mobile-menu__button items ──
    var mobileNav = document.querySelector('.mobile-menu');
    if (mobileNav && isVisible(mobileNav)) {
      // Cart link in mobile is class="mobile-menu__button cart__toggle".
      // We want our heart RIGHT BEFORE the cart icon, matching desktop.
      var mobileCart = mobileNav.querySelector('a[href="/cart"], a[href*="/cart"]');
      if (mobileCart) {
        return { mode: 'before-mobile', parent: mobileNav, before: mobileCart };
      }
      // Fallback: append to mobile menu (will land at end, after hamburger).
      return { mode: 'append-mobile', target: mobileNav };
    }

    // ── Desktop: #NavStandard with .menu__item--icon wrappers ──
    var navRoot = document.getElementById('NavStandard') ||
                  document.querySelector('nav.nav') ||
                  document.querySelector('header nav');
    if (navRoot) {
      var iconWrappers = navRoot.querySelectorAll('.menu__item--icon');
      var visibleIconWrappers = [];
      for (var i = 0; i < iconWrappers.length; i++) {
        if (isVisible(iconWrappers[i])) visibleIconWrappers.push(iconWrappers[i]);
      }
      // Prefer the cart wrapper if we can identify it.
      var cartWrapper = null;
      for (var j = 0; j < visibleIconWrappers.length; j++) {
        if (visibleIconWrappers[j].querySelector('a[href="/cart"], a[href*="/cart"]')) {
          cartWrapper = visibleIconWrappers[j];
          break;
        }
      }
      // Fallback to last visible if cart wasn\'t identified.
      if (!cartWrapper && visibleIconWrappers.length > 0) {
        cartWrapper = visibleIconWrappers[visibleIconWrappers.length - 1];
      }
      if (cartWrapper && cartWrapper.parentElement) {
        return { mode: 'before', parent: cartWrapper.parentElement, before: cartWrapper };
      }
    }

    // ── Strategy 3: SWYM placeholder, only if no nav was found above ──
    var swymSlot = document.getElementById('swym-inject-header-parent') ||
                   document.getElementById('swym-inject-header-mobile') ||
                   document.getElementById('swym-inject-header-desktop');
    if (swymSlot) {
      swymSlot.setAttribute('style', 'display: inline-flex !important; visibility: visible !important;');
      return { mode: 'fill', target: swymSlot };
    }

    // ── Strategy 4: header utility containers as a final fallback ──
    var iconsRow = document.querySelector('.menu__item--icons, .header__icons, [class*="header-actions"]');
    if (iconsRow) {
      return { mode: 'append', target: iconsRow };
    }

    return null;
  }

  // Visibility check: element is rendered with non-zero size. Skips
  // mobile-only or desktop-only DOM elements that are display:none on
  // the current viewport.
  function isVisible(el) {
    if (!el) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function injectNavHeart() {
    if (document.querySelector('.bopf-nav-heart')) return; // already injected
    var spot = findNavInjectionPoint();
    if (!spot) {
      log('nav injection point not found, retrying shortly');
      // Many themes lazy-render the header; retry briefly. After 4
      // seconds give up silently.
      var retries = 0;
      var t = setInterval(function() {
        retries++;
        if (document.querySelector('.bopf-nav-heart') || retries > 20) {
          clearInterval(t);
          return;
        }
        var newSpot = findNavInjectionPoint();
        if (newSpot) {
          clearInterval(t);
          mountNavHeart(newSpot);
        }
      }, 200);
      return;
    }
    mountNavHeart(spot);
  }

  function mountNavHeart(spot) {
    var count = getSavedCount();
    var a = document.createElement('a');
    // For mobile, add the theme's mobile button class so spacing matches.
    var isMobile = (spot.mode === 'before-mobile' || spot.mode === 'append-mobile');
    a.className = 'bopf-nav-heart' +
                  (isMobile ? ' bopf-nav-heart--mobile mobile-menu__button' : '') +
                  (count > 0 ? ' has-saved' : '');
    a.href = '/pages/saved';
    a.setAttribute('aria-label', count > 0 ? ('Saved pieces (' + count + ')') : 'Saved pieces');
    a.innerHTML = HEART_SVG +
      '<span class="bopf-nav-heart-badge" data-bopf-count>' + (count > 99 ? '99+' : count) + '</span>';

    if (spot.mode === 'fill') {
      spot.target.innerHTML = '';
      spot.target.appendChild(a);
    } else if (spot.mode === 'before') {
      // Desktop: wrap in the theme\'s menu__item--icon div for spacing parity.
      var wrapper = document.createElement('div');
      wrapper.className = 'menu__item menu__item--icon';
      wrapper.appendChild(a);
      spot.parent.insertBefore(wrapper, spot.before);
    } else if (spot.mode === 'append') {
      var wrapper2 = document.createElement('div');
      wrapper2.className = 'menu__item menu__item--icon';
      wrapper2.appendChild(a);
      spot.target.appendChild(wrapper2);
    } else if (spot.mode === 'before-mobile') {
      // Mobile: heart is a direct child of .mobile-menu, sibling of the
      // other .mobile-menu__button anchors. No wrapper div.
      spot.parent.insertBefore(a, spot.before);
    } else if (spot.mode === 'append-mobile') {
      spot.target.appendChild(a);
    }
    log('nav heart injected, count=' + count + ', mode=' + spot.mode);

    // Reactive updates from same-tab saves/removes.
    window.addEventListener('bopf-wishlist-changed', function() {
      updateNavHeart(true);
    });
    // Reactive updates from other tabs (localStorage propagates).
    window.addEventListener('storage', function(e) {
      if (e.key === 'bopf_zara_saved') updateNavHeart(true);
    });
  }

  function updateNavHeart(animate) {
    var heart = document.querySelector('.bopf-nav-heart');
    if (!heart) return;
    var count = getSavedCount();
    var prevCount = parseInt(heart.getAttribute('data-prev-count') || '0', 10);
    var badge = heart.querySelector('[data-bopf-count]');
    if (badge) badge.textContent = count > 99 ? '99+' : count;
    if (count > 0) heart.classList.add('has-saved');
    else heart.classList.remove('has-saved');
    heart.setAttribute('aria-label', count > 0 ? ('Saved pieces (' + count + ')') : 'Saved pieces');

    // Pulse only when count INCREASES (a save happened).
    // Decreases (removes) shouldn't celebrate.
    if (animate && count > prevCount) {
      heart.classList.remove('pulse'); // restart the animation
      void heart.offsetWidth; // force reflow so the class re-add re-triggers
      heart.classList.add('pulse');
      setTimeout(function() { heart.classList.remove('pulse'); }, 450);
    }
    heart.setAttribute('data-prev-count', String(count));
  }

  log('v10 script parsed, readyState =', document.readyState);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();